/**
 * Tax config persistence.
 *
 * A single global settings doc (`bank_tax_config/config`) — the cashflow data
 * (bank_transactions/rules) is app-global, so the tax settings are too. Writes
 * are merge + debounced so dragging a slider doesn't hammer Firestore. The
 * collection is permitted by the firestore.rules catch-all (no rule change).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, getDoc, getFirestore, serverTimestamp, setDoc } from 'firebase/firestore';
import { FinancialYear, CapitalAsset } from '../utils/auTax';
import { CategoryTaxOverrides } from '../utils/taxProfile';

const COLL = 'bank_tax_config';
const DOC_ID = 'config';

export interface TaxConfig {
  financialYear: FinancialYear;
  gstRegistered: boolean;
  /** Fraction (0..1) of net partnership income to partner A. */
  splitA: number;
  partnerAName: string;
  partnerBName: string;
  dependents: number;
  otherIncomeA: number;
  otherIncomeB: number;
  deductionsA: number;
  deductionsB: number;
  /** Deductible personal (concessional) super contributions per partner. */
  superA: number;
  superB: number;
  /** Business-use fraction of the home used as the business premises (0..1). */
  premisesPct: number;
  /** Annual home-occupancy costs NOT in the bank feed — the premisesPct share
   *  of each is an additional partnership deduction. */
  homeMortgageInterest: number;
  homeRates: number;
  homeInsurance: number;
  homeCleaning: number;
  /** Catch-all annual business deductions not captured by transactions
   *  (office fit-out, bad debts, financing interest, costs paid elsewhere). */
  otherBusinessDeductions: number;
  /** Treat expense:wages as deductible employee wages (default: drawings). */
  wagesAreEmployees: boolean;
  /** Per-category treatment overrides keyed by category id. */
  categoryOverrides: CategoryTaxOverrides;
  /** Depreciating assets + capital works (Div 40 / Div 43). */
  capitalAssets: CapitalAsset[];
}

export const DEFAULT_TAX_CONFIG: TaxConfig = {
  financialYear: '2025-26',
  gstRegistered: true,
  splitA: 0.5,
  partnerAName: 'Partner 1',
  partnerBName: 'Partner 2',
  dependents: 0,
  otherIncomeA: 0,
  otherIncomeB: 0,
  deductionsA: 0,
  deductionsB: 0,
  superA: 0,
  superB: 0,
  premisesPct: 0.2,
  homeMortgageInterest: 0,
  homeRates: 0,
  homeInsurance: 0,
  homeCleaning: 0,
  otherBusinessDeductions: 0,
  wagesAreEmployees: false,
  categoryOverrides: {},
  capitalAssets: [],
};

/** Merge a stored doc over the defaults, tolerating missing/renamed fields. */
function coerce(data: any): TaxConfig {
  if (!data || typeof data !== 'object') return { ...DEFAULT_TAX_CONFIG };
  const fy: FinancialYear = data.financialYear === '2024-25' ? '2024-25' : '2025-26';
  const num = (v: any, d: number) => (Number.isFinite(Number(v)) ? Number(v) : d);
  return {
    financialYear: fy,
    gstRegistered: data.gstRegistered !== false,
    splitA: Math.min(1, Math.max(0, num(data.splitA, 0.5))),
    partnerAName: typeof data.partnerAName === 'string' && data.partnerAName ? data.partnerAName : DEFAULT_TAX_CONFIG.partnerAName,
    partnerBName: typeof data.partnerBName === 'string' && data.partnerBName ? data.partnerBName : DEFAULT_TAX_CONFIG.partnerBName,
    dependents: Math.max(0, Math.round(num(data.dependents, 0))),
    otherIncomeA: num(data.otherIncomeA, 0),
    otherIncomeB: num(data.otherIncomeB, 0),
    deductionsA: num(data.deductionsA, 0),
    deductionsB: num(data.deductionsB, 0),
    superA: num(data.superA, 0),
    superB: num(data.superB, 0),
    premisesPct: Math.min(1, Math.max(0, num(data.premisesPct, 0.2))),
    homeMortgageInterest: num(data.homeMortgageInterest, 0),
    homeRates: num(data.homeRates, 0),
    homeInsurance: num(data.homeInsurance, 0),
    homeCleaning: num(data.homeCleaning, 0),
    otherBusinessDeductions: num(data.otherBusinessDeductions, 0),
    wagesAreEmployees: !!data.wagesAreEmployees,
    categoryOverrides: data.categoryOverrides && typeof data.categoryOverrides === 'object' ? data.categoryOverrides : {},
    capitalAssets: Array.isArray(data.capitalAssets) ? data.capitalAssets : [],
  };
}

export interface UseTaxConfig {
  config: TaxConfig;
  loaded: boolean;
  saving: boolean;
  update: (patch: Partial<TaxConfig>) => void;
  /** Convenience for the per-category treatment table. */
  setCategoryOverride: (categoryId: string, patch: { deductible?: boolean; gstApplicable?: boolean } | null) => void;
}

export function useTaxConfig(): UseTaxConfig {
  const [config, setConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const snap = await getDoc(doc(getFirestore(), COLL, DOC_ID));
        if (mounted.current && snap.exists()) setConfig(coerce(snap.data()));
      } catch {
        // fall back to defaults — the view still works, just unsaved
      } finally {
        if (mounted.current) setLoaded(true);
      }
    })();
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const persist = useCallback((next: TaxConfig) => {
    if (timer.current) clearTimeout(timer.current);
    setSaving(true);
    timer.current = setTimeout(async () => {
      try {
        await setDoc(
          doc(getFirestore(), COLL, DOC_ID),
          { ...next, updatedAt: serverTimestamp() },
          { merge: true },
        );
      } catch {
        // ignore — the in-memory state is still correct for this session
      } finally {
        if (mounted.current) setSaving(false);
      }
    }, 600);
  }, []);

  const update = useCallback((patch: Partial<TaxConfig>) => {
    setConfig((prev) => {
      const next = { ...prev, ...patch };
      persist(next);
      return next;
    });
  }, [persist]);

  const setCategoryOverride = useCallback((categoryId: string, patch: { deductible?: boolean; gstApplicable?: boolean } | null) => {
    setConfig((prev) => {
      const overrides = { ...prev.categoryOverrides };
      if (patch === null) {
        delete overrides[categoryId];
      } else {
        overrides[categoryId] = { ...overrides[categoryId], ...patch };
      }
      const next = { ...prev, categoryOverrides: overrides };
      persist(next);
      return next;
    });
  }, [persist]);

  return { config, loaded, saving, update, setCategoryOverride };
}
